import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { apiCreateProfile } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, Camera, Mic2, Globe, X, Check, Loader2, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const RELAZIONI = [
  { value: "padre", label: "Padre" },
  { value: "madre", label: "Madre" },
  { value: "nonno", label: "Nonno" },
  { value: "nonna", label: "Nonna" },
  { value: "fratello", label: "Fratello" },
  { value: "sorella", label: "Sorella" },
  { value: "figlio", label: "Figlio" },
  { value: "figlia", label: "Figlia" },
  { value: "marito", label: "Marito" },
  { value: "moglie", label: "Moglie" },
  { value: "amico", label: "Amico/a" },
  { value: "altro", label: "Altro" },
];

const STEP_LABELS = ["Informazioni", "Foto", "Social", "Vocali"];

export default function ProfileCreatePage() {
  const { token } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [nome, setNome] = useState("");
  const [soprannome, setSoprannome] = useState("");
  const [eta, setEta] = useState("");
  const [relazione, setRelazione] = useState("");

  const [photos, setPhotos] = useState([]);
  const photoInputRef = useRef(null);

  const [socialLinks, setSocialLinks] = useState(["", ""]);

  const [audioFiles, setAudioFiles] = useState([]);
  const audioInputRef = useRef(null);

  const addPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 5) { toast.error("Massimo 5 foto"); return; }
    setPhotos((prev) => [...prev, ...files]);
  };
  const removePhoto = (idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx));

  const addAudio = (e) => {
    const files = Array.from(e.target.files || []);
    if (audioFiles.length + files.length > 5) { toast.error("Massimo 5 vocali"); return; }
    setAudioFiles((prev) => [...prev, ...files]);
  };
  const removeAudio = (idx) => setAudioFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!nome || !relazione) { toast.error("Compila nome e relazione"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("nome", nome);
      fd.append("soprannome", soprannome);
      if (eta) fd.append("eta", eta);
      fd.append("relazione", relazione);
      fd.append("social_links", JSON.stringify(socialLinks.filter(Boolean)));
      photos.forEach((p) => fd.append("photos", p));
      audioFiles.forEach((a) => fd.append("audio_files", a));
      const profile = await apiCreateProfile(token, fd);
      toast.success(`Profilo di ${profile.nome} creato!`);
      nav(`/chat/${profile.id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const canNext = () => {
    if (step === 1) return nome && relazione;
    return true;
  };

  return (
    <div className="min-h-screen bg-gradient-hero px-6 py-6" data-testid="profile-create-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="sm" onClick={() => step > 1 ? setStep(step - 1) : nav("/dashboard")}
            className="text-muted-foreground hover:text-foreground" data-testid="profile-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> {step > 1 ? "Indietro" : "Dashboard"}
          </Button>
          <span className="text-xs text-muted-foreground">Passo {step} di 4</span>
        </div>

        {/* Progress */}
        <div className="flex gap-1.5 mb-2">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`h-1 flex-1 rounded-full transition-all duration-500 ${
              s <= step ? "bg-accent" : "bg-border/50"
            }`} />
          ))}
        </div>
        <div className="flex justify-between mb-8">
          {STEP_LABELS.map((label, i) => (
            <span key={i} className={`text-[10px] ${i + 1 <= step ? "text-accent" : "text-muted-foreground/40"}`}>{label}</span>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
            {/* Step 1: Info */}
            {step === 1 && (
              <Card className="bg-card border-border/30 shadow-soft">
                <CardContent className="p-7 space-y-5">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Upload className="w-4 h-4 text-accent" />
                    </div>
                    <h2 className="font-serif text-lg font-bold text-foreground">Chi vuoi rivivere?</h2>
                  </div>
                  <div className="space-y-2">
                    <Label>Nome *</Label>
                    <Input placeholder="Nome del tuo caro" value={nome} onChange={(e) => setNome(e.target.value)} data-testid="input-nome" />
                  </div>
                  <div className="space-y-2">
                    <Label>Soprannome</Label>
                    <Input placeholder="Come lo/la chiamavi?" value={soprannome} onChange={(e) => setSoprannome(e.target.value)} data-testid="input-soprannome" />
                  </div>
                  <div className="space-y-2">
                    <Label>Eta</Label>
                    <Input type="number" placeholder="Eta" value={eta} onChange={(e) => setEta(e.target.value)} data-testid="input-eta" />
                  </div>
                  <div className="space-y-2">
                    <Label>Relazione *</Label>
                    <Select value={relazione} onValueChange={setRelazione}>
                      <SelectTrigger data-testid="select-relazione"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {RELAZIONI.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Photos */}
            {step === 2 && (
              <Card className="bg-card border-border/30 shadow-soft">
                <CardContent className="p-7">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Camera className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <h2 className="font-serif text-lg font-bold text-foreground">Foto</h2>
                      <p className="text-[11px] text-muted-foreground">Carica foto del tuo caro (max 5)</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {photos.map((p, i) => (
                      <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-secondary border border-border/30">
                        <img src={URL.createObjectURL(p)} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => removePhoto(i)}
                          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-foreground/60 text-background flex items-center justify-center hover:bg-foreground transition-colors">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {photos.length < 5 && (
                      <button onClick={() => photoInputRef.current?.click()} data-testid="add-photo-btn"
                        className="aspect-square rounded-xl border-2 border-dashed border-border/50 hover:border-accent/40 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
                        <Camera className="w-5 h-5" />
                        <span className="text-[10px]">Aggiungi</span>
                      </button>
                    )}
                  </div>
                  <input ref={photoInputRef} type="file" accept="image/*" multiple className="hidden" onChange={addPhoto} />
                  <p className="text-[11px] text-muted-foreground/50 mt-2">Consiglio: foto frontali dove si vede bene il volto</p>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Social */}
            {step === 3 && (
              <Card className="bg-card border-border/30 shadow-soft">
                <CardContent className="p-7">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      <Globe className="w-4 h-4 text-emerald-500" />
                    </div>
                    <div>
                      <h2 className="font-serif text-lg font-bold text-foreground">Profili Social</h2>
                      <p className="text-[11px] text-muted-foreground">Link ai social del tuo caro (opzionale)</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {socialLinks.map((link, i) => (
                      <div key={i} className="space-y-1">
                        <Label className="text-xs">Profilo {i + 1}</Label>
                        <Input placeholder="https://facebook.com/..." value={link}
                          onChange={(e) => { const n = [...socialLinks]; n[i] = e.target.value; setSocialLinks(n); }}
                          data-testid={`social-link-${i}`} />
                      </div>
                    ))}
                    {socialLinks.length < 5 && (
                      <Button variant="ghost" size="sm" onClick={() => setSocialLinks([...socialLinks, ""])} className="text-accent text-xs">
                        + Aggiungi profilo
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 4: Audio */}
            {step === 4 && (
              <Card className="bg-card border-border/30 shadow-soft">
                <CardContent className="p-7">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Mic2 className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-serif text-lg font-bold text-foreground">Messaggi vocali</h2>
                      <p className="text-[11px] text-muted-foreground">Carica vocali per clonare la voce (opzionale)</p>
                    </div>
                  </div>
                  <div className="space-y-2 mb-4">
                    {audioFiles.map((a, i) => (
                      <div key={i} className="flex items-center gap-3 bg-secondary/50 rounded-lg px-4 py-3 border border-border/20">
                        <Mic2 className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="text-sm text-foreground flex-1 truncate">{a.name}</span>
                        <span className="text-[10px] text-muted-foreground">{(a.size / 1024).toFixed(0)} KB</span>
                        <button onClick={() => removeAudio(i)} className="text-muted-foreground/40 hover:text-destructive transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => audioInputRef.current?.click()} data-testid="add-audio-btn"
                    className="w-full py-5 rounded-xl border-2 border-dashed border-border/50 hover:border-primary/30 flex flex-col items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
                    <Upload className="w-5 h-5" />
                    <span className="text-sm">Carica file audio</span>
                    <span className="text-[10px] text-muted-foreground/50">MP3, WAV, OGG, M4A</span>
                  </button>
                  <input ref={audioInputRef} type="file" accept="audio/*" multiple className="hidden" onChange={addAudio} />
                  <p className="text-[11px] text-muted-foreground/50 mt-3">Consiglio: vocali da WhatsApp dove si sente bene la voce</p>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <div />
          {step < 4 ? (
            <Button variant="premium" size="lg" onClick={() => setStep(step + 1)} disabled={!canNext()} data-testid="next-step-btn">
              Continua <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button variant="warm" size="lg" onClick={handleSubmit} disabled={loading} data-testid="create-profile-btn">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Creazione...</> : <><Check className="w-4 h-4 mr-1.5" /> Crea Profilo</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
